import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { EditableDBItemV3, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, ProtoComponent } from '@nu-art/thunderstorm-frontend/index';
import { TS_HorizontalWorkspace, TS_Workspace } from '@nu-art/ts-workspace-frontend/index';
import { ICONS, SearchSelector_BaseManagerUI } from '@app/styles';
import { cloneObj, DefaultNewItemId, exists, resolveContent } from '@nu-art/ts-common';
import { getAdvisorUIManagerSearchItems, workspaceKey_Page_AdvisorUI } from './consts.js';
import { cleanFAQ, cleanFindingView, DBDef_FAQ, DBDef_FindingView } from '@app/advisor-ui-shared';
import { Editor_FindingView } from '../Editor_FindingView/Editor_FindingView.js';
import './Page_AdvisorUI.scss';
import { ConfigDialogKeys_AdvisorUI, ConfigDialogs_AdvisorUI_Dialogs, ConfigDialogs_AdvisorUI_Labels } from '../config-dialogs/index.js';
import { Editor_FAQ } from '../Editor_FAQ/Editor_FAQ.js';
import { ModuleFE_FindingView } from '../../_entity/finding-view/ModuleFE_FindingView.js';
import { ModuleFE_FAQ } from '../../_entity/frequently-asked-question/ModuleFE_FAQ.js';
export class Page_AdvisorUI extends ProtoComponent {
    static defaultProps = {
        keys: ['selected', 'searchItem'],
    };
    // ######################## Life Cycle ########################
    deriveStateFromProps(nextProps, state) {
        state.searchItems ??= getAdvisorUIManagerSearchItems();
        //Get selectedSearchItem from url
        const selected = this.getQueryParam('selected', {});
        //Get selected search item
        state.selectedSearchItem = undefined;
        for (const searchItem of state.searchItems) {
            const selectedId = selected[searchItem.type];
            if (!exists(selectedId))
                continue;
            state.selectedSearchItem = { type: searchItem.type, id: selectedId };
            break;
        }
        return state;
    }
    // ######################## Logic ########################
    onSearchItemSelected = (item) => {
        const selected = { ...this.getQueryParam('selected', {}) };
        //Clear all selections under this manager
        this.state.searchItems.forEach(searchItem => delete selected[searchItem.type]);
        //Set the selected item
        selected[item.type] = item.id;
        this.setQueryParams({
            selected: selected,
            searchItem: item.type, //Switch the filters in the search panel to the item type
        });
    };
    createNewItem = (dbKey) => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
        this.onSearchItemSelected({ type: dbKey, id: DefaultNewItemId });
    };
    getEditable = (module, newItemBase) => {
        const id = this.state.selectedSearchItem?.id;
        if (!id)
            return;
        const selectedItem = module.cache.unique(id) ?? cloneObj(resolveContent(newItemBase));
        const editable = new EditableDBItemV3(selectedItem, module);
        editable.setAutoSave(selectedItem._id !== DefaultNewItemId);
        editable.setDebounceTimeout(300);
        editable.setOnSaveCompleted(item => {
            //If the item is newly created, simulate selection for it
            if (id === DefaultNewItemId)
                return this.onSearchItemSelected({ type: module.dbDef.dbKey, id: item._id });
            //Item is not newly created, re-render
            this.forceUpdate();
        });
        return editable;
    };
    deselectItem = (dbKey) => {
        const selected = { ...this.getQueryParam('selected', {}) };
        delete selected[dbKey];
        this.setQueryParam('selected', selected);
    };
    // ######################## Render ########################
    render() {
        return _jsx(TS_Workspace, { workspaceKey: workspaceKey_Page_AdvisorUI, renderers: {
                horizontalSpace: TS_HorizontalWorkspace,
            }, instances: {
                list: () => this.renderList(),
                editor: () => this.renderEditor(),
            } });
    }
    renderList = () => {
        return _jsx(SearchSelector_BaseManagerUI, { buttons: [{
                    label: () => _jsx(ICONS.advisor_avn_gear.component, {}),
                    action: (e) => {
                        const buttonBox = e.currentTarget.getBoundingClientRect();
                        const model = {
                            id: 'advisor-context-editor__add-new-popup',
                            originPos: {
                                x: buttonBox.x + buttonBox.width + 10,
                                y: buttonBox.y + (buttonBox.height / 2),
                            },
                            modalPos: { x: 1, y: 0 },
                            content: () => {
                                return _jsx(_Fragment, { children: ConfigDialogKeys_AdvisorUI.map(key => {
                                        const label = ConfigDialogs_AdvisorUI_Labels[key];
                                        const dialog = ConfigDialogs_AdvisorUI_Dialogs[key];
                                        return _jsx("div", { onClick: () => {
                                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                                                dialog.show();
                                            }, children: label }, key);
                                    }) });
                            },
                        };
                        ModuleFE_MouseInteractivity.showContent(model);
                    }
                }, {
                    label: 'Add New',
                    action: (e) => {
                        const buttonBox = e.currentTarget.getBoundingClientRect();
                        const model = {
                            id: 'advisor-context-editor__add-new-popup',
                            originPos: {
                                x: buttonBox.x + buttonBox.width + 10,
                                y: buttonBox.y + (buttonBox.height / 2),
                            },
                            modalPos: { x: 1, y: 0 },
                            content: () => {
                                return _jsx(_Fragment, { children: this.state.searchItems.map(searchItem => _jsx("div", { onClick: () => this.createNewItem(searchItem.type), children: searchItem.entityLabel }, searchItem.type)) });
                            },
                        };
                        ModuleFE_MouseInteractivity.showContent(model);
                    }
                }], searchItems: this.state.searchItems, onSearchItemSelected: this.onSearchItemSelected, selectedItem: this.state.selectedSearchItem });
    };
    renderEditor = () => {
        switch (this.state.selectedSearchItem?.type) {
            case DBDef_FindingView.dbKey:
                return this.renderEditor_FindingView();
            case DBDef_FAQ.dbKey:
                return this.renderEditor_FAQ();
            default:
                return _jsx(_Fragment, {});
        }
    };
    renderEditor_FindingView = () => {
        const editable = this.getEditable(ModuleFE_FindingView, cleanFindingView);
        if (!editable)
            return _jsx(_Fragment, {});
        return _jsx(Editor_FindingView, { editable: editable, deselect: () => this.deselectItem(DBDef_FindingView.dbKey) });
    };
    renderEditor_FAQ = () => {
        const editable = this.getEditable(ModuleFE_FAQ, cleanFAQ);
        if (!editable)
            return _jsx(_Fragment, {});
        return _jsx(Editor_FAQ, { editable: editable, deselect: () => this.deselectItem(DBDef_FAQ.dbKey) });
    };
}
//# sourceMappingURL=Page_AdvisorUI.js.map